import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ContentCopy as CopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Refresh as ResetIcon,
  OpenInNew as OpenIcon,
} from "@mui/icons-material";
import type { Portal } from "@/types";
import { useLogViewer, type ELKCredentials } from "../hooks/useLogViewer";

interface LogViewerTabProps {
  portal: Portal;
}

const LogViewerTab: React.FC<LogViewerTabProps> = ({ portal }) => {
  const {
    elkConfig,
    isEnabled,
    enableLogViewer,
    isEnabling,
    resetPassword,
    isResetting,
    disableLogViewer,
    isDisabling,
  } = useLogViewer(portal);

  const [newCredentials, setNewCredentials] = useState<ELKCredentials | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCopy = (value: string, label: string) => {
    navigator.clipboard.writeText(value);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleEnable = async () => {
    setError(null);
    try {
      const result = await enableLogViewer();
      setNewCredentials(result);
      setShowPassword(true);
    } catch (err) {
      const msg = (err as { message?: string })?.message || "Failed to enable log viewer";
      setError(msg);
    }
  };

  const handleResetPassword = async () => {
    setError(null);
    setConfirmReset(false);
    try {
      const result = await resetPassword();
      setNewCredentials(result);
      setShowPassword(true);
    } catch (err) {
      const msg = (err as { message?: string })?.message || "Failed to reset password";
      setError(msg);
    }
  };

  const handleDisable = () => {
    setError(null);
    disableLogViewer(undefined, {
      onSuccess: () => {
        setConfirmDisable(false);
        setNewCredentials(null);
      },
      onError: (err: unknown) => {
        const msg = (err as { message?: string })?.message || "Failed to disable log viewer";
        setError(msg);
        setConfirmDisable(false);
      },
    });
  };

  const kibanaUrl = newCredentials?.kibana_url || elkConfig?.kibana_url;

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="body1" fontWeight={600}>
            Log Viewer (Kibana)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Access application logs, audit trail, and request logs for this portal via Kibana.
          </Typography>
        </Box>
        <Chip
          label={isEnabled ? "Enabled" : "Not Configured"}
          color={isEnabled ? "success" : "default"}
          size="small"
        />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Not enabled — show enable button */}
      {!isEnabled && (
        <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Log viewer is not configured for this portal. Enable it to create
            a dedicated Kibana space with isolated access to this portal's logs.
          </Typography>
          <Button
            variant="contained"
            onClick={handleEnable}
            disabled={isEnabling}
          >
            {isEnabling ? "Setting up..." : "Enable Log Viewer"}
          </Button>
        </Paper>
      )}

      {/* Enabled — show credentials */}
      {isEnabled && (
        <Stack spacing={3}>
          {/* New credentials alert */}
          {newCredentials && (
            <Alert severity="warning" sx={{ "& .MuiAlert-message": { width: "100%" } }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Kibana credentials — save these now
              </Typography>
              <Stack spacing={1.5}>
                <CredentialField
                  label="Username"
                  value={newCredentials.user}
                  copied={copied}
                  onCopy={handleCopy}
                />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Password
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <TextField
                      value={showPassword ? newCredentials.password : "••••••••••••••••"}
                      size="small"
                      fullWidth
                      slotProps={{
                        input: {
                          readOnly: true,
                          sx: { fontFamily: "monospace", fontSize: "0.85rem" },
                        },
                      }}
                    />
                    <Tooltip title={showPassword ? "Hide" : "Show"}>
                      <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={copied === "password" ? "Copied!" : "Copy"}>
                      <IconButton size="small" onClick={() => handleCopy(newCredentials.password, "password")}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              </Stack>
            </Alert>
          )}

          {/* Credentials & Access */}
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
              Access Details
            </Typography>
            <Stack spacing={2}>
              <CredentialField
                label="Kibana URL"
                value={kibanaUrl ?? "—"}
                copied={copied}
                onCopy={handleCopy}
              />
              <CredentialField
                label="Username"
                value={elkConfig?.user ?? "—"}
                copied={copied}
                onCopy={handleCopy}
              />
              <CredentialField
                label="Space"
                value={elkConfig?.space_id ?? "—"}
                copied={copied}
                onCopy={handleCopy}
              />
            </Stack>

            <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }}>
              {kibanaUrl && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<OpenIcon />}
                  href={kibanaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open Kibana
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                startIcon={<ResetIcon />}
                onClick={() => setConfirmReset(true)}
                disabled={isResetting}
              >
                {isResetting ? "Resetting..." : "Reset Password"}
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={() => setConfirmDisable(true)}
                disabled={isDisabling}
              >
                {isDisabling ? "Disabling..." : "Disable Log Viewer"}
              </Button>
            </Stack>
          </Paper>

          {/* Info */}
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              What's Available
            </Typography>
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary">
                • <strong>Application logs</strong> — HTTP requests, errors, and service events
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • <strong>Audit trail</strong> — User actions (create, update, delete) with who/what/when
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • <strong>Portal isolation</strong> — Only logs for this portal are visible
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Filter by: <code>event_type: "audit"</code> for audit trail only
              </Typography>
            </Stack>
          </Paper>
        </Stack>
      )}

      {/* Confirm Reset Dialog */}
      <Dialog open={confirmReset} onClose={() => setConfirmReset(false)}>
        <DialogTitle>Reset Kibana Password</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will generate a new password and immediately invalidate the current one.
            Anyone using the old password will need to be updated.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmReset(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleResetPassword} disabled={isResetting}>
            {isResetting ? "Resetting..." : "Reset Password"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Disable Dialog */}
      <Dialog open={confirmDisable} onClose={() => setConfirmDisable(false)}>
        <DialogTitle>Disable Log Viewer</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will delete the Kibana space, user account, and access role for this portal.
            Log data in Elasticsearch will NOT be deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDisable(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDisable} disabled={isDisabling}>
            {isDisabling ? "Disabling..." : "Disable"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/** Read-only field with copy button */
const CredentialField: React.FC<{
  label: string;
  value: string;
  copied: string | null;
  onCopy: (value: string, label: string) => void;
}> = ({ label, value, copied, onCopy }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Typography
        variant="body2"
        sx={{ fontFamily: "monospace", fontSize: "0.85rem", wordBreak: "break-all", flex: 1 }}
      >
        {value}
      </Typography>
      {value !== "—" && (
        <Tooltip title={copied === label ? "Copied!" : "Copy"}>
          <IconButton size="small" onClick={() => onCopy(value, label)}>
            <CopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  </Box>
);

export default LogViewerTab;
